/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { 
  Server, 
  Check, 
  X, 
  RefreshCw, 
  Download, 
  Copy, 
  AlertCircle,
  ExternalLink 
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  VelocityMcpAgent,
  VelocityMcpAgentStatus,
  getVelocityMcpStatus,
  installVelocityMcp,
} from '../../services/skillshop';

interface AgentInfo {
  id: VelocityMcpAgent;
  name: string;
  description: string;
  docsUrl?: string;
}

const AGENTS: AgentInfo[] = [
  {
    id: 'claude-code',
    name: 'Claude Code',
    description: 'Anthropic\'s AI coding assistant',
    docsUrl: 'https://docs.anthropic.com/claude-code',
  },
  {
    id: 'cursor',
    name: 'Cursor',
    description: 'AI-powered code editor',
    docsUrl: 'https://docs.cursor.com/context/model-context-protocol',
  },
  {
    id: 'codex',
    name: 'OpenAI Codex',
    description: 'OpenAI\'s Codex CLI tool',
    docsUrl: 'https://developers.openai.com/codex',
  },
  {
    id: 'antigravity',
    name: 'AntiGravity',
    description: 'Google\'s AI coding IDE',
    docsUrl: 'https://antigravity.codes',
  },
];

export function VelocityMcpInstallSettings() {
  const { toast } = useToast();
  const [statuses, setStatuses] = useState<VelocityMcpAgentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [installingAgent, setInstallingAgent] = useState<VelocityMcpAgent | null>(null);
  const [copiedAgent, setCopiedAgent] = useState<VelocityMcpAgent | null>(null);
  const [expandedAgent, setExpandedAgent] = useState<VelocityMcpAgent | null>(null);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const result = await getVelocityMcpStatus();
      setStatuses(result);
    } catch (error) {
      console.error('Failed to fetch Velocity MCP status:', error);
      toast({ title: 'Failed to load installation status', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const handleInstall = async (agent: VelocityMcpAgent) => {
    setInstallingAgent(agent);
    try {
      const result = await installVelocityMcp(agent);
      
      if (result.success) {
        if (result.alreadyInstalled) {
          toast({ title: `Velocity is already installed in ${result.displayName}` });
        } else {
          toast({ title: `Successfully installed Velocity to ${result.displayName}` });
        }
        // Refresh statuses
        await fetchStatuses();
      } else {
        toast({ 
          title: result.error || `Failed to install to ${result.displayName}`, 
          variant: 'destructive' 
        });
        // Show manual installation option
        setExpandedAgent(agent);
      }
    } catch (error) {
      console.error('Install failed:', error);
      toast({ title: 'Installation failed. Try manual installation.', variant: 'destructive' });
      setExpandedAgent(agent);
    } finally {
      setInstallingAgent(null);
    }
  };

  const handleCopyConfig = (agent: VelocityMcpAgent) => {
    const status = statuses.find(s => s.agent === agent);
    if (!status?.configSnippet) return;
    
    navigator.clipboard.writeText(status.configSnippet);
    setCopiedAgent(agent);
    toast({ title: 'Config copied to clipboard' });
    
    setTimeout(() => setCopiedAgent(null), 2000);
  };

  const getStatusForAgent = (agentId: VelocityMcpAgent): VelocityMcpAgentStatus | undefined => {
    return statuses.find(s => s.agent === agentId);
  };

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f0f7ff',
      borderRadius: '8px',
      border: '1px solid #b3d4fc'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Server size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
          <h3 style={{ margin: 0 }}>Install Velocity MCP Server</h3>
        </div>
        <button
          onClick={fetchStatuses}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 12px',
            fontSize: '12px',
            background: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            color: '#6b7280'
          }}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          Refresh
        </button>
      </div>

      <p style={{ 
        margin: '0 0 16px 0', 
        fontSize: '14px', 
        color: '#4b5563',
        lineHeight: '1.5'
      }}>
        Connect your AI coding tools to Velocity's MCP server for enhanced product context, 
        user stories, and issue management directly in your IDE.
      </p>

      {loading ? (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '40px',
          color: '#6b7280'
        }}>
          <RefreshCw size={20} className="spin" style={{ marginRight: '8px' }} />
          Loading installation status...
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
          gap: '12px' 
        }}>
          {AGENTS.map(agent => {
            const status = getStatusForAgent(agent.id);
            const isInstalling = installingAgent === agent.id;
            const isExpanded = expandedAgent === agent.id;
            const isCopied = copiedAgent === agent.id;
            
            return (
              <div 
                key={agent.id}
                style={{
                  padding: '16px',
                  background: '#fff',
                  borderRadius: '8px',
                  border: status?.installed ? '1px solid #10b981' : '1px solid #e5e7eb',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
                        {agent.name}
                      </h4>
                      {status?.installed && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 6px',
                          fontSize: '11px',
                          fontWeight: 500,
                          background: '#d1fae5',
                          color: '#047857',
                          borderRadius: '4px'
                        }}>
                          <Check size={12} />
                          Installed
                        </span>
                      )}
                    </div>
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: '12px', 
                      color: '#6b7280' 
                    }}>
                      {agent.description}
                    </p>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  <button
                    onClick={() => handleInstall(agent.id)}
                    disabled={isInstalling}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: 500,
                      background: status?.installed ? '#f3f4f6' : '#0066cc',
                      color: status?.installed ? '#374151' : '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isInstalling ? 'wait' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isInstalling ? (
                      <>
                        <RefreshCw size={14} className="spin" />
                        Installing...
                      </>
                    ) : status?.installed ? (
                      <>
                        <RefreshCw size={14} />
                        Reinstall
                      </>
                    ) : (
                      <>
                        <Download size={14} />
                        Install
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setExpandedAgent(isExpanded ? null : agent.id)}
                    style={{
                      padding: '8px',
                      fontSize: '13px',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Show manual installation"
                  >
                    <AlertCircle size={14} />
                  </button>
                </div>

                {isExpanded && status && (
                  <div style={{ 
                    marginTop: '12px',
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontWeight: 500, color: '#374151' }}>
                        Config file: <code style={{ 
                          background: '#e5e7eb', 
                          padding: '2px 4px', 
                          borderRadius: '3px',
                          fontSize: '11px'
                        }}>{status.configPath}</code>
                      </span>
                    </div>
                    
                    <pre style={{
                      margin: '8px 0',
                      padding: '8px',
                      background: '#1f2937',
                      color: '#e5e7eb',
                      borderRadius: '4px',
                      fontSize: '11px',
                      overflow: 'auto',
                      maxHeight: '150px'
                    }}>
                      {status.configSnippet}
                    </pre>
                    
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleCopyConfig(agent.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 10px',
                          fontSize: '11px',
                          background: isCopied ? '#d1fae5' : '#fff',
                          color: isCopied ? '#047857' : '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {isCopied ? <Check size={12} /> : <Copy size={12} />}
                        {isCopied ? 'Copied!' : 'Copy Config'}
                      </button>
                      
                      {agent.docsUrl && (
                        <a
                          href={agent.docsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 10px',
                            fontSize: '11px',
                            background: '#fff',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            textDecoration: 'none'
                          }}
                        >
                          <ExternalLink size={12} />
                          Docs
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
